/**
 * 環境変数設定
 *
 * Vite環境変数を管理
 */

export const ENV = {
  // アプリケーション設定
  APP_NAME: import.meta.env.NEXT_PUBLIC_APP_NAME || "商談記録アシスタント",
  VERSION: import.meta.env.NEXT_PUBLIC_VERSION || "0.0.1",

  // 環境判定
  NODE_ENV: import.meta.env.NODE_ENV || "development",
  IS_DEVELOPMENT: import.meta.env.NODE_ENV === "development",
  IS_PRODUCTION: import.meta.env.NODE_ENV === "production",
  IS_TEST: import.meta.env.NODE_ENV === "test",

  // デプロイ環境識別（dev/stg/prod）
  VITE_ENV: (import.meta.env.VITE_ENV as string | undefined) || "dev",

  // デバッグ設定
  DEBUG_MODE:
    import.meta.env.NEXT_PUBLIC_DEBUG_MODE === "true" ||
    import.meta.env.NODE_ENV === "development",
  DEBUG_LOG_LEVEL: import.meta.env.NEXT_PUBLIC_DEBUG_LOG_LEVEL || "info",
  MAX_DEBUG_LOGS: parseInt(
    import.meta.env.NEXT_PUBLIC_MAX_DEBUG_LOGS || "1000",
    10,
  ),
  SHOW_DEBUG_INFO:
    import.meta.env.NEXT_PUBLIC_SHOW_DEBUG_INFO === "true" ||
    import.meta.env.NODE_ENV === "development",

  // セキュリティ設定
  HTTPS_ONLY:
    import.meta.env.HTTPS_ONLY === "true" ||
    import.meta.env.NODE_ENV === "production",
  SECURE_COOKIES:
    import.meta.env.SECURE_COOKIES === "true" ||
    import.meta.env.NODE_ENV === "production",

  // API設定
  API_TIMEOUT: parseInt(import.meta.env.API_TIMEOUT || "30000", 10), // 30秒
  MAX_FILE_SIZE: parseInt(import.meta.env.MAX_FILE_SIZE || "10485760", 10), // 10MB

  // リアルタイムAPI設定
  REALTIME_SESSION_MAX_DURATION: parseInt(
    import.meta.env.REALTIME_SESSION_MAX_DURATION || "900000",
    10,
  ), // 15分（900秒）
  REALTIME_SESSION_WARNING_TIME: parseInt(
    import.meta.env.REALTIME_SESSION_WARNING_TIME || "600000",
    10,
  ), // 10分（600秒）
  REALTIME_RECONNECT_INTERVAL: parseInt(
    import.meta.env.REALTIME_RECONNECT_INTERVAL || "30000",
    10,
  ), // 30秒
  REALTIME_KEEPALIVE_INTERVAL: parseInt(
    import.meta.env.REALTIME_KEEPALIVE_INTERVAL || "30000",
    10,
  ), // 30秒

  // データ設定
  DATA_DIR: import.meta.env.DATA_DIR || "./data",
  BACKUP_DIR: import.meta.env.BACKUP_DIR || "./backups",

  // プラットフォーム検出
  IS_VERCEL: !!import.meta.env.VERCEL,
  IS_AWS: !!import.meta.env.AWS_REGION,

  // レート制限
  RATE_LIMIT_REQUESTS: parseInt(
    import.meta.env.RATE_LIMIT_REQUESTS || "100",
    10,
  ),
  RATE_LIMIT_WINDOW_MS: parseInt(
    import.meta.env.RATE_LIMIT_WINDOW_MS || "900000",
    10,
  ), // 15分

  // OpenAI Model設定
  // RealtimeConfig が0件のテナント時の最終フォールバック
  OPENAI_MODEL:
    import.meta.env.VITE_OPENAI_MODEL || "gpt-realtime-1.5",
  OPENAI_VOICE: import.meta.env.VITE_OPENAI_VOICE || "verse",

  // トークンと検証コードの交換用の自前エンドポイント
  AUTH_PORTAL_URL: import.meta.env.VITE_AUTH_PORTAL_URL,
  COGNITO_USERPOOL_ID: import.meta.env.VITE_COGNITO_USERPOOL_ID,
  CLIENT_ID: import.meta.env.VITE_COGNITO_CLIENT_ID,
  // PRODUCT_ID: "devserver", // 開発用
  PRODUCT_ID: import.meta.env.VITE_PRODUCT_ID,
  PRODUCT_API_ENDPOINT: import.meta.env.VITE_PRODUCT_API_ENDPOINT,

  // PRODUCT_DEV-493: CloudWatch RUM（フロント計測）。未設定の環境では RUM 無効。
  RUM_APP_MONITOR_ID: import.meta.env.VITE_RUM_APP_MONITOR_ID as
    | string
    | undefined,
  RUM_IDENTITY_POOL_ID: import.meta.env.VITE_RUM_IDENTITY_POOL_ID as
    | string
    | undefined,
  RUM_REGION:
    (import.meta.env.VITE_RUM_REGION as string | undefined) || "ap-northeast-1",
  NOTIFICATION_API_ENDPOINT:
    import.meta.env.VITE_NOTIFICATION_API_ENDPOINT || "",
  // Admin API（UnizoAdminServiceStack: 独立したAPI Gateway）
  ADMIN_API_ENDPOINT: import.meta.env.VITE_ADMIN_API_ENDPOINT,
} as const;

// ログレベルの定義
export const LOG_LEVELS = {
  error: 0,
  warn: 1,
  info: 2,
  debug: 3,
} as const;

export type LogLevel = keyof typeof LOG_LEVELS;

// ログレベルチェック関数
export const shouldLog = (level: LogLevel): boolean => {
  const currentLevel =
    LOG_LEVELS[ENV.DEBUG_LOG_LEVEL as LogLevel] ?? LOG_LEVELS.info;
  return LOG_LEVELS[level] <= currentLevel;
};
